<template lang="pug">
div(v-if="audioDoc")
p(v-else-if="wordDoc && !captions") [ #[i Video player] ]
.video-transcript-doc(uc-word-break-after="" v-else-if="wordDoc")
  p.title
    uc-text(text="Video transcript" uc-word-bold="")

  //- This block gets replaced once the transcript is processed for the doc
  .transcript.block(:class="screen.id ?? id")
    .captions.block(
      :data-screen="screen.id ?? id"
      :data-video="src"
      :href="captions"
      v-if="captions"
    )
      uc-text(:text="screen.id ?? id" type="p")
      uc-text(:text="`Captions for transcript: ${captions}`" uc-word-italic="" type="p")

.video-container.bg-gray-50.dark_bg-gray-900.relative(
  ref="videoContainerRef"
  :class="[showTranscript ? 'flex flex-col' : 'aspect-video h-full max-h-[80vh] mx-auto', { 'max-h-screen': screen['no-wrap'], 'lg_flex-row': !lightbox }, { 'overflow-hidden corners': !screen['no-wrap'] }]"
  :style="videoIconVars"
  v-else=""
)
  .video(
    v-if="!useFallbackPlayer"
    :class="[{ 'aspect-video lg_flex-[3]': showTranscript }]"
  )
    uc-videojs-tech(
      ref="techRef"
      :data-test="captionsPath"
      :video-class="{ '-no-captions-available': !captions, 'force-video-completion': enforceVideoCompletion }"
      v-if="!wordDoc && playerHostVisible"
    )

  .video-fallback(v-if="!wordDoc && useFallbackPlayer")
    video(
      ref="fallbackRef"
      :id="id"
      :src="translatedSrc"
      controls="true"
      playsinline="true"
      :muted="muteFallback"
    )
      track(v-if="nativeCaptions" :src="nativeCaptions.src" kind="subtitles" default :srclang="nativeCaptions.lang" :label="nativeCaptions.label")

  //- expose these strings for the translate script
  //- text="Video transcript"
  .transcript.relative.lg_flex-1(
    :class="[{ 'hidden': !showTranscript }]"
    :ref="setTranscriptRef"
    tabindex="0"
    role="region"
    :aria-label="hasTranslation('Video transcript')"
    v-if="captions && transcriptReady && !useFallbackPlayer"
  )
    .transcript-sr.sr-only(
      v-if="captions && transcriptReady"
    )
      uc-text(:text="transcriptString")
    .lg_absolute.lg_w-full.lg_h-full.lg_top-0.lg_left-0.ml-auto.mr-auto.my-2.lg_m-0.px-4.overflow-y-auto.overflow-x-hidden.simple-scrollbar.prose.max-w-prose.dark_text-gray-200.text-sm(
      class="min-h-[10rem] max-h-[30vh] lg_max-h-full"
      aria-hidden="true"
    )
      .title.text-center.my-2
        uc-text.font-bold(text="Video transcript")
      span(v-for="(cue, c) in currentCues")
        //- use a div with top padding for spacing between paras (but not on the very first cue)
        .pt-4(v-if="c > 0 && cue.sentenceStart")
        span.cue.transition-colors(
          :class="[{ 'pe-1': !cue.sentenceEnd }, `cue-index-${c}`]"
        ) {{ cue.cleanText }}
        .lg_pb-4(v-if="c === currentCues.length - 1")

  .video-next.absolute.flex.flex-col.gap-5.left-0.top-0.w-full.h-full.items-center.justify-center.auto-alpha-0(
    :class="['bg-white/70 dark_bg-black/70', { 'auto-alpha-1': showOverlayNextButton }]"
    v-if="shouldShowOverlayNextButton"
  )
    uc-button.btn-sm.btn-primary.py-2.px-5.text-gray-100.dark_text-gray-900.ripple(
      class="bg-gray-800/70 dark_bg-gray-200/70"
      icon="mdi:replay"
      @click="replay"
    )
      uc-text(text="Replay")

    uc-preload-sfx(name="continue")
    uc-button.btn-lg.btn-primary.unlock.ripple(
      :class="content?.course['button-color-from-closest-primary'] ? '' : content?.course?.primary && `primary-${content?.course?.primary}`"
      data-sfx="continue"
    )
      uc-text(
        :k="buttonText ? `${screen.id}.continue` : 'Next'"
        :key="1"
        :text="buttonText || 'Next'"
          )
</template>

<script setup lang="ts">
import ScrollTrigger from 'gsap/ScrollTrigger';
import { setCompleted, set, get } from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';
import { throttle } from 'lodash-es';
import {
  inject,
  onBeforeUnmount,
  provide,
  ref,
  unref,
  watch,
  nextTick,
  computed,
  reactive,
  type Ref
} from 'vue';
import type VideoJs from 'video.js';
import useContent from '../stores/useContent';
import bugAlert from '../lib/bugAlert';
import useTranslations from '../stores/useTranslations';
import type { WatchStopHandle } from 'vue';
import { useSafeLocalStorage } from '../lib/safeLocalStorage';
import { storeToRefs } from 'pinia';
import isQuizPage from '../lib/isQuizPage';
import { stop as stopAudio } from '../lib/audio';
import expandVttUploadPath from '../lib/expandVttUploadPath';
import findVttPathForLocale from '../lib/findVttPathForLocale';
import UcVideojsTech from './uc-videojs-tech.vue';
import Iconify from '@iconify/iconify';
import useIcons from '../stores/useIcons';

const { compiled: content } = storeToRefs(useContent());
const { translations } = useTranslations();
const suspend = useSuspend();
const volume = useSafeLocalStorage('uc-video-volume', 1);
const defaultLanguage = content.value?.['default-language'];

const languages = content.value?.languages?.length
  ? content.value.languages
  : defaultLanguage
    ? [defaultLanguage]
    : ['en-GB'];

const props = defineProps<{
  src: string;
  id: string;
  options?: VideoJs.PlayerOptions;
  type?: string;
  controls?: boolean;
  captions?: string;
  autoScroll?: boolean;
  lightbox?: boolean;
  isTitleVideo?: boolean;
  screen: UcScreen & {
    'autoplay-video'?: string;
  };
}>();

const { resolveIcon, themeIcons } = useIcons();

// themed video-control icons, mapped to CSS vars and consumed as masks in
// <style>. Each value is a theme:* key resolved + rendered to a data-URI.
const VIDEO_ICON_VARS: Record<string, string> = {
  '--vjs-play-icon': 'theme:video-play',
  '--vjs-pause-icon': 'theme:video-pause',
  '--vjs-volume-icon': 'theme:audio-on',
  '--vjs-mute-icon': 'theme:audio-off',
  '--vjs-fullscreen-icon': 'theme:video-fullscreen',
  '--vjs-fullscreen-exit-icon': 'theme:video-fullscreen-exit',
  '--vjs-quality-icon': 'theme:video-quality',
  '--vjs-quality-sd-icon': 'theme:video-quality-sd',
  '--vjs-captions-icon': 'theme:video-captions',
  '--vjs-transcript-icon': 'theme:video-transcript',
  '--vjs-speedometer-icon': 'theme:video-speed'
};

// Resolve a theme:* key to a mask data-URI. Bundled icons render synchronously;
// unbundled ones are fetched from the Iconify API (same fallback as uc-icon.vue).
const resolveIconUrl = async (themeKey: string) => {
  const icon = resolveIcon(themeKey);
  let svg = Iconify.renderSVG(icon, {})?.outerHTML;
  if (!svg) {
    svg = await fetch(
      `https://api.iconify.design/${icon.replace(':', '/')}.svg`
    )
      .then(r => r.text())
      .catch(() => '');
    if (!svg?.startsWith('<svg')) return undefined;
  }
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const videoIconVars = ref<Record<string, string>>({});

const buildVideoIconVars = async () => {
  const entries = await Promise.all(
    Object.entries(VIDEO_ICON_VARS).map(async ([cssVar, themeKey]) => [
      cssVar,
      await resolveIconUrl(themeKey)
    ])
  );
  videoIconVars.value = Object.fromEntries(
    entries.filter(([, url]) => url)
  ) as Record<string, string>;
};

// rebuild on any change to the resolved icon map (theme / icon-set switch),
// not a single key — most icon-sets don't override the video:* keys, so
// watching one resolved string would never fire on a theme change
watch(themeIcons, buildVideoIconVars, {
  immediate: true,
  deep: true
});

const translatedSrc = computed(
  () => translations?.[suspend.locale]?.[props.src] || props.src
);

// Expand the captions path to the content base for captions that have been translated and moved to S3
const captionsPath = computed(() => {
  if (props.captions) {
    return expandVttUploadPath(props.captions);
  }
  return undefined;
});

const nativeCaptions = ref({});

// Update nativeCaptions when locale or captionsPath changes
watch(
  [() => suspend.locale, captionsPath],
  async () => {
    if (captionsPath.value) {
      let computedCaptions = {};
      for (const lang of languages) {
        if (suspend.locale === lang) {
          computedCaptions.src =
            languages.length > 1
              ? await findVttPathForLocale(captionsPath.value, lang)
              : captionsPath.value;
          computedCaptions.label = lang;
          computedCaptions.srclang = lang;
        }
      }
      nativeCaptions.value = computedCaptions;
    }
  },
  { immediate: true }
);

const showTranscript = computed(() => {
  return suspend.transcript && captionsPath.value;
});

const showOverlayNextButton = ref(false);
const forceShowOverlayNextButton = ref(false);

const useFallbackPlayer = ref(false);
const fallbackRef = ref<HTMLMediaElement>();

const muteFallback = computed(() => {
  return suspend.mute ?? suspend.videoMute ?? false;
});

const shouldShowOverlayNextButton = computed(
  () =>
    (props.screen['no-wrap'] && forceShowOverlayNextButton.value) ||
    (props.screen['no-wrap'] && !props.autoScroll && !props.lightbox)
);

const buttonText = computed(() =>
  typeof props.screen?.continue === 'string'
    ? props.screen?.continue
    : typeof props.screen?.start === 'string'
      ? props.screen?.start
      : null
);

//- expose these strings for the translate script
//- text="Play"
//- text="Mute"
//- text="Unmute"
//- text="Fullscreen"
//- text="Video Quality setting"
//- text="Captions"
//- text="Captions selected"
//- text="Transcript"
//- text="Transcript selected"
const playButtonTitle = computed(() => hasTranslation('Play'));
const muteButtonTitle = computed(() =>
  hasTranslation(suspend.videoMute ? 'Unmute' : 'Mute')
);
const fullscreenButtonTitle = computed(() => hasTranslation('Fullscreen'));
const videoQualityButtonTitle = computed(() =>
  hasTranslation('Video Quality setting')
);
const captionsButtonTitle = computed(() =>
  hasTranslation(suspend.captions ? 'Captions selected' : 'Captions')
);
const transcriptButtonTitle = computed(() =>
  hasTranslation(suspend.transcript ? 'Transcript selected' : 'Transcript')
);

const transcriptReady = ref(false);

const enforceVideoCompletion = computed(() => {
  return (
    content.value.course['enforce-video-completion'] === true &&
    !suspend.completed[sectionId]
  );
});

let autoPlayCount = ref(suspend.completed[props.id] ? 1 : 0);

const canAutoPlay = computed(() => {
  let autoPlay = true;
  const autoPlayLimit = props.screen['autoplay-video'];

  if (autoPlayLimit) {
    autoPlay =
      autoPlayLimit === 'always' || autoPlayLimit === 'true'
        ? true
        : autoPlayLimit === 'never' || autoPlayLimit === 'false'
          ? false
          : autoPlayLimit === 'once'
            ? autoPlayCount.value < 1
            : false;
  }
  return autoPlay;
});

const setTranscriptRef = (el: HTMLElement) => {
  return (transcriptElement = el);
};

function seekToCue(cue) {
  getPlayer().currentTime(cue.startTime + 0.01);
}

function replay() {
  showOverlayNext(false);
  getPlayer()?.currentTime(0);
  play(getPlayer());
}

function showOverlayNext(show) {
  if (
    !shouldShowOverlayNextButton.value ||
    show === showOverlayNextButton.value
  )
    return;

  showOverlayNextButton.value = show;

  getPlayer()?.controls(show ? false : props.controls);
}

function scrollCueIntoView(cueEl: HTMLElement) {
  // find the best-supported scroll functionality
  if (cueEl.scrollIntoViewIfNeeded) {
    cueEl.scrollIntoViewIfNeeded(true);
  } else if (cueEl.scrollIntoView) {
    cueEl.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
  } else {
    transcriptElement.scrollTo({
      behavior: 'smooth',
      top: cueEl.offsetTop
    });
  }
}

const playerCache: { [id: string]: VideoJs.Player } = {};
const minHD = 720;

const sectionId = inject('sectionId') as string;
const pageId = inject('pageId') as object;
const wordDoc = inject('wordDoc');
const audioDoc = inject('audioDoc');

/**
 * Lazy-init: mount player while this section is the active one or an immediate neighbour
 * (see uc-scroll-section `sectionVideoKeepAlive`) — fewer live players than full unit, smoother scroll than active-only.
 */
const sectionVideoKeepAlive = inject<Ref<boolean>>(
  'sectionVideoKeepAlive',
  () => ref(true),
  true
);
const playerHostVisible = computed(() => unref(sectionVideoKeepAlive));

function isAppleTouchWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }
  return false;
}

const highQualityAvailable = ref(null);
const techRef = ref<null | {
  videoEl?: HTMLVideoElement | Ref<HTMLVideoElement | null>;
}>(null);
const videoContainerRef = ref<HTMLDivElement | null>(null);

function getTechVideoEl(): HTMLVideoElement | null {
  const inst = techRef.value as {
    videoEl?: HTMLVideoElement | Ref<HTMLVideoElement | null>;
  } | null;
  if (!inst?.videoEl) return null;
  return unref(inst.videoEl as Ref<HTMLVideoElement | null>);
}

let currentCues = ref([]);
let transcriptString = ref('');
let transcriptElement: HTMLElement = reactive({}) as HTMLElement;

function getPlayer(): VideoJs.Player {
  return playerCache[props.id];
}

function destroyPlayer(): void {
  if (playerCache[props.id]) {
    playerCache?.[props.id]?.dispose();
    delete playerCache[props.id];
  }
}

function stripTags(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

// for ensuring the video isnt completed in less time than its duration
let firstPlayedTimestamp: number;

const play = (player?: VideoJs.Player) => {
  if (player)
    player.play()?.catch(() => {
      bugAlert(
        `A video failed to play, probably because of the browser's autoplay policy. This is unlikely to be an issue for real users! \n\nLearn more: http://lms.vinciworks.com/awscourses_test/docs/author/video.html#autoplay`
      );
    });
};

const stopTranslationWatcher = ref<WatchStopHandle>();
const stopCaptionWatcher = ref<WatchStopHandle>();
const stopTranscriptWatcher = ref<WatchStopHandle>();
const stopVideoMuteWatcher = ref<WatchStopHandle>();
const stopVolumeWatcher = ref<WatchStopHandle>();

function isEndOfSentence(text: string) {
  if (!text) return false;

  return (
    text.endsWith('.') ||
    text.endsWith('!') ||
    text.endsWith('?') ||
    text.endsWith(')') ||
    text.endsWith('."')
  );
}

const emit = defineEmits(['ended']);

function hasTranslation(key: string | number, fallback: string | number = key) {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];
  return maybeTranslated || key;
}

let playerSetupGeneration = 0;
let viewportVideoST: ScrollTrigger | null = null;
let quizResetHandler: ((e: CustomEvent) => void) | null = null;
let transcriptRetryTimer: ReturnType<typeof setTimeout> | null = null;

function clearPlayerWatchers() {
  stopCaptionWatcher.value?.();
  stopCaptionWatcher.value = undefined;
  stopTranscriptWatcher.value?.();
  stopTranscriptWatcher.value = undefined;
  stopVideoMuteWatcher.value?.();
  stopVideoMuteWatcher.value = undefined;
  stopVolumeWatcher.value?.();
  stopVolumeWatcher.value = undefined;
  stopTranslationWatcher.value?.();
  stopTranslationWatcher.value = undefined;
}

function fullVideoPlayerTeardown() {
  playerSetupGeneration++;
  if (transcriptRetryTimer) {
    clearTimeout(transcriptRetryTimer);
    transcriptRetryTimer = null;
  }
  const appEl = document.querySelector('#app');
  if (quizResetHandler && appEl) {
    appEl.removeEventListener('quizReset', quizResetHandler);
    quizResetHandler = null;
  }
  viewportVideoST?.kill();
  viewportVideoST = null;
  destroyPlayer();
  clearPlayerWatchers();
  transcriptReady.value = false;
  currentCues.value = [];
  transcriptString.value = '';
}

provide('ucVideoTechUnmount', () => {
  fullVideoPlayerTeardown();
});

const complete = (force: boolean = false) => {
  // enforce video ends to complete
  if (enforceVideoCompletion.value && !force) {
    // if less time has passed since the first play event than the duration of the video, then the delay should be the difference
    const playedAt = firstPlayedTimestamp ?? Date.now();
    const timeSinceFirstPlayed = Date.now() - playedAt;
    let durationMs = 0;
    try {
      const p = getPlayer();
      const d = p?.duration?.();
      if (typeof d === 'number' && Number.isFinite(d) && d > 0) {
        durationMs = d * 1000;
      }
    } catch {
      /* player may be disposed */
    }
    const delay =
      durationMs > 0
        ? Math.max(0, durationMs * 0.95 - timeSinceFirstPlayed)
        : 0;

    setTimeout(() => {
      setCompleted(props.id);
      setCompleted(sectionId);
    }, delay);
  } else {
    setCompleted(props.id);
    setCompleted(sectionId);
  }

  // on ended or error
  if (force) {
    showOverlayNext(true);
  }
};

// save the current video time to scorm (throttled to every 5 seconds)
const updateScorm = throttle((currentTime: number) => {
  set(`video.${props.id}.currentTime`, currentTime);
}, 5e3);

// check if we have seen enough of the video to show the next button (throttled to every 1 second)
const updateCompletion = throttle((player: VideoJs.Player) => {
  if (!player) return;

  const t = player?.currentTime() ?? 0;
  const d = player?.duration() ?? 0;
  const r = d - t;

  // Mark complete at 90% progress for videos less than 1 minute,
  // or with 5 secs remaining for videos over 1 minute.
  if (d > 0 && ((d < 60 && t / d > 0.9) || (d >= 60 && r <= 5))) {
    complete();
  }
}, 1e3);

watch(
  () => playerHostVisible.value,
  async keep => {
    if (!keep || unref(wordDoc) || unref(audioDoc)) return;
    // After a Video.js error we use native fallback; clear that so the next visit
    // remounts tech and runs setup again instead of staying stuck with no <video>.
    useFallbackPlayer.value = false;
    await nextTick();
    if (!playerHostVisible.value) return;
    let el = getTechVideoEl();
    if (!el) {
      await nextTick();
      el = getTechVideoEl();
    }
    if (!el || !playerHostVisible.value) return;
    if (isAppleTouchWebKit()) {
      await new Promise<void>(resolve =>
        requestAnimationFrame(() => setTimeout(resolve, 0))
      );
    }
    if (!playerHostVisible.value || !getTechVideoEl()) return;
    await runPlayerSetup();
  },
  { flush: 'post', immediate: true }
);

async function runPlayerSetup() {
  playerSetupGeneration++;
  const myGen = playerSetupGeneration;
  const videoContainer = unref(videoContainerRef);
  const el = getTechVideoEl();
  if (!el || !videoContainer) return;

  const fallbackEl = unref(fallbackRef);

  function stale() {
    return myGen !== playerSetupGeneration;
  }

  function disposeIfStale(created: VideoJs.Player | undefined) {
    if (!stale()) return false;
    if (created) {
      try {
        created.dispose();
      } catch {
        /* noop */
      }
    }
    delete playerCache[props.id];
    return true;
  }

  const { videojs } = await import('../lib/videojs');
  if (disposeIfStale(undefined)) return;

  // reset the overlay next button state (force shown on error)
  showOverlayNextButton.value = false;
  forceShowOverlayNextButton.value = false;

  const src = translatedSrc.value;
  const useNativeHls = isAppleTouchWebKit() && /\.m3u8(\?|$)/i.test(src.trim());

  const player: VideoJs.Player = videojs(
    el,
    {
      controls: props.controls ?? true,
      fill: true,
      playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
      ...props.options,
      nativeTextTracks: false,
      html5: { hls: { overrideNative: !useNativeHls } },
      sources: [
        { type: src.includes('.m3u8') ? 'application/x-mpegURL' : '', src }
      ]
    },

    function onPlayerReady() {
      this.muted(suspend.mute ?? suspend.videoMute ?? false);
      this.volume(volume.value ?? 1);

      const seekstep = enforceVideoCompletion.value ? 0 : 5;
      this.hotkeys({
        volumeStep: 0.1,
        seekStep: seekstep,
        enableModifiersForNumbers: false,
        enableInactiveFocus: false
      });
    }
  );

  stopCaptionWatcher.value = watch(
    () => suspend.locale,
    () => {
      // turn on captions when showing a translation (if transcript is also off)
      if (
        suspend.locale !== 'en-GB' &&
        !get('captions') &&
        !get('transcript')
      ) {
        set('captions', true);
      }

      updateControlButtonCaptions();

      // update selected language captions track and transcript
      updateTextTracks();
    }
  );

  stopTranscriptWatcher.value = watch(
    () => suspend.transcript,
    t => {
      // turn on captions when hiding the transcript (if muted)
      if (!t && !get('captions') && suspend.videoMute) {
        set('captions', true);
      }
    }
  );

  stopVideoMuteWatcher.value = watch(
    () => suspend.videoMute,
    () => {
      if (!suspend.videoMute && player.volume() === 0) player.volume(0.5); // avoid unmuting to a zero volume

      // when video is muted turn on captions (if transcript is also off)
      if (suspend.videoMute && !get('captions') && !get('transcript')) {
        set('captions', true);
      }

      // mute/unmute all player instances
      videojs.getAllPlayers().forEach(p => p.muted(suspend.videoMute));
    }
  );

  stopVolumeWatcher.value = watch(
    () => volume.value,
    () => {
      // update the volume on all player instances
      videojs.getAllPlayers().forEach(p => p.volume(volume.value));
    }
  );

  stopTranslationWatcher.value = watch(translatedSrc, (value, oldValue) => {
    if (value !== oldValue && player.src() !== value) {
      const paused = player.paused();
      player.src(value.trim());
      if (!paused) player.play();
    }
  });

  function addCCButton() {
    var Button = videojs.getComponent('Button');
    var CaptionsButton = videojs.extend(Button, {
      constructor: function () {
        Button.apply(this, arguments);
        this.controlText(captionsButtonTitle.value);
        this.addClass('vjs-control');
        this.addClass('vjs-captions-button');
      },
      handleClick: function () {
        set('captions', !suspend.captions);
        setControlButtonTitle(
          '.vjs-captions-button',
          captionsButtonTitle.value
        );
      }
    });
    try {
      videojs.registerComponent('captionsButton', CaptionsButton);
    } catch {
      /* already registered */
    }
    player?.getChild('controlBar')?.addChild('captionsButton', {});
  }

  function addTranscriptButton() {
    var Button = videojs.getComponent('Button');
    var TranscriptButton = videojs.extend(Button, {
      constructor: function () {
        Button.apply(this, arguments);
        this.controlText(transcriptButtonTitle.value);
        this.addClass('vjs-chapters-button'); // for the icon
        this.addClass('vjs-control');
        this.addClass('vjs-transcript-button');
      },
      handleClick: function () {
        // toggle transcript on/off
        set('transcript', !suspend.transcript);
        setControlButtonTitle(
          '.vjs-transcript-button',
          transcriptButtonTitle.value
        );
      }
    });
    try {
      videojs.registerComponent('transcriptButton', TranscriptButton);
    } catch {
      /* already registered */
    }
    player?.getChild('controlBar')?.addChild('transcriptButton', {});
  }

  function createTranscript(track) {
    if (stale()) return;
    const activePlayer = getPlayer();
    if (!activePlayer || (activePlayer as any)?.isDisposed?.()) return;

    let activeCues;
    try {
      activeCues = track?.activeCues;
    } catch {
      return;
    }

    // check until track cues are loaded/available
    if (!activeCues) {
      if (transcriptRetryTimer) clearTimeout(transcriptRetryTimer);
      transcriptRetryTimer = setTimeout(() => {
        transcriptRetryTimer = null;
        createTranscript(track);
      }, 100);
    } else {
      if (transcriptRetryTimer) {
        clearTimeout(transcriptRetryTimer);
        transcriptRetryTimer = null;
      }
      let cues;
      try {
        cues = track.cues;
      } catch {
        return;
      }
      if (!cues) return;
      currentCues.value = [];
      try {
        for (let c = 0; c < cues.length; c++) {
          if (stale()) return;
          const currentCue = cues[c];
          const previousCue = cues[c - 1];
          const previousCueGap = Number(
            Number(
              previousCue !== null
                ? currentCue?.startTime - previousCue?.endTime
                : -1
            ).toFixed(2)
          );
          const nextCue = cues[c + 1];
          const nextCueGap = Number(
            Number(
              nextCue !== null ? nextCue?.startTime - currentCue.endTime : -1
            ).toFixed(2)
          );

          currentCue.cleanText = stripTags(currentCue.text);
          currentCue.sentenceStart =
            c === 0 ||
            isEndOfSentence(previousCue?.text) ||
            previousCueGap > 0.5;
          currentCue.sentenceEnd =
            c === cues.length - 0 ||
            isEndOfSentence(currentCue?.text) ||
            nextCueGap > 0.5;

          /*if (import.meta.env.DEV) {
            const cueData = {
              index: c,
              start: currentCue.startTime,
              end: currentCue.endTime,
              pGap: previousCueGap,
              nGap: nextCueGap,
              isStart: currentCue.sentenceStart,
              isEnd: currentCue.sentenceEnd,
              text: currentCue?.text
            };
            console.log(cueData);
          }*/

          currentCues.value.push(currentCue);
        }
        transcriptString.value = currentCues.value
          .map(c => c.cleanText)
          .join(' ');
        transcriptReady.value = true;
      } catch (e) {
        console.log(e);
      }
    }
  }

  function updateTextTracks() {
    if (stale()) return;
    const activePlayer = getPlayer();
    if (!activePlayer || (activePlayer as any)?.isDisposed?.()) return;
    // Get all text tracks for the current player.
    let tracks;
    try {
      tracks = player.remoteTextTracks();
    } catch {
      return;
    }

    for (var i = 0; i < tracks.length; i++) {
      var track = tracks[i];
      track.mode = 'disabled';

      if (track.kind === 'captions' && track.language === suspend.locale) {
        // show the currently selected language captions track (if captions are turned on)
        track.mode = suspend.captions ? 'showing' : 'disabled';

        // create the transcript for the current captions
        createTranscript(track);
      }
    }
  }

  function updateControlButtonCaptions() {
    setControlButtonTitle('.vjs-play-control', playButtonTitle.value);
    setControlButtonTitle('.vjs-mute-control', muteButtonTitle.value);
    setControlButtonTitle(
      '.vjs-fullscreen-control',
      fullscreenButtonTitle.value
    );
    setControlButtonTitle(
      '.vjs-quality-selector > button.vjs-menu-button-popup.vjs-button',
      videoQualityButtonTitle.value
    );
    setControlButtonTitle('.vjs-captions-button', captionsButtonTitle.value);
    setControlButtonTitle(
      '.vjs-transcript-button',
      transcriptButtonTitle.value
    );
  }

  function timeUpdate() {
    if (showTranscript.value) {
      highlightActiveCue(player.currentTime());
    }

    updateScorm(player.currentTime());
    updateCompletion(player);
  }

  function highlightActiveCue(currentTime) {
    if (transcriptElement?.querySelector) {
      for (let c = 0; c < currentCues.value.length; c++) {
        const cue: VTTCue = currentCues.value[c];
        const cueEl: HTMLElement = transcriptElement?.querySelector(
          `.cue-index-${c}`
        ) as HTMLElement;

        if (currentTime >= cue.startTime && currentTime <= cue.endTime) {
          cueEl.classList.add('active');

          scrollCueIntoView(cueEl);
        } else {
          cueEl.classList.remove('active');
        }
      }
    }
  }

  function setControlButtonTitle(btnSel, title) {
    const controlBar = player?.getChild('controlBar');
    const button = controlBar?.el()?.querySelector(btnSel);

    if (button) {
      button.setAttribute('title', title);
    }
  }

  await new Promise(r => player.ready(r as VideoJs.ReadyCallback));
  if (disposeIfStale(player)) return;

  // disable skipping the video if we are enforcing video completion
  if (enforceVideoCompletion.value) {
    try {
      player.controlBar.progressControl.disable();
    } catch (error) {
      console.log('Error disabling video controls: ', error);
    }
  }

  playerCache[props.id] = player;
  player.src(src.trim());

  player.on('play', () => {
    if (!firstPlayedTimestamp) {
      firstPlayedTimestamp = Date.now();
    }

    // prevent odd video play happening if you skip to the end and the overlay-next appears, the video would start playing below
    if (shouldShowOverlayNextButton.value && showOverlayNextButton.value) {
      player.pause();
    }

    // prevent BSL video from playing when a video template is played
    document.dispatchEvent(
      new CustomEvent('activeAudioChanged', { detail: { activeId: '' } })
    );

    // stop current audio on the course title screen
    if (props.isTitleVideo) {
      stopAudio();
    }

    autoPlayCount.value = autoPlayCount.value + 1;
  });

  player.on('canplay', () => {
    // when ready, update the button titles
    updateControlButtonCaptions();

    const vEl = getTechVideoEl();
    if (
      vEl &&
      vEl.offsetHeight > 0 &&
      vEl.closest('.slide--active') &&
      onScreen &&
      canAutoPlay.value &&
      !suspend.reducedMotion
    ) {
      play(player);
    }
  });

  player.on('error', async e => {
    // TODO retry?
    console.error('Video player error: ', e);
    useFallbackPlayer.value = true;
    forceShowOverlayNextButton.value = true;
    await nextTick();
    complete(true);
    try {
      console.log('Adding fallback player error listener');
      fallbackEl?.addEventListener('error', () => {
        console.error('Fallback player failed to load');
        complete(true);
      });

      fallbackEl?.addEventListener('ended', () => {
        complete(true);
      });
    } catch (error) {
      console.error('Fallback player error: ', error);
      complete(true);
    }
  });

  player.on('volumechange', () => {
    try {
      if (player.muted() || player.volume() === 0) {
        set('videoMute', true);
      } else if (!player.muted()) {
        set('videoMute', false);
      }

      // save to local storage
      volume.value = Number(player.volume().toFixed(1));
    } catch (e) {
      console.warn(e);
    }
  });

  player.on('timeupdate', timeUpdate);
  player.on('ended', () => {
    complete(true);
    showOverlayNext(true);
    emit('ended');
  });

  player.on('ended', () => {
    try {
      if (player.isFullscreen()) {
        player.exitFullscreen();
      }
    } catch (e) {
      console.warn(e);
    }
  });

  player.on('ended', async () => {
    if (props['autoScroll']) {
      const scrollEl = getTechVideoEl();
      await nextTick();
      await nextTick();
      await nextTick();

      const slides = scrollEl?.closest('.slides');

      if (slides && scrollEl?.matches('.slide:not(:last-child) *')) {
        slides.next();
      } else {
        const page = scrollEl?.closest('.page') as UcPageElement;
        page?.unlockAndScroll(sectionId);
      }
    }
  });

  // TODO - better handling of fallback player, not just return
  if (!useFallbackPlayer.value) {
    addCCButton();
    addTranscriptButton();
  }

  /**
   * If we have multiple languages we assume default is en-GB and add a lang suffix to all the other caption paths
   * For a single language YAML we do not add a caption name suffix - this could be for the default English or a German localisation
   */
  if (captionsPath.value) {
    for (const lang of languages) {
      if (disposeIfStale(player)) return;
      try {
        const captionSrc = await findVttPathForLocale(captionsPath.value, lang);
        player.addRemoteTextTrack(
          {
            default: lang === suspend.locale,
            src: captionSrc,
            kind: 'captions',
            label: lang,
            srclang: lang,
            id: props.id + '_' + lang
          },
          true
        );
      } catch (e) {
        console.log(
          `Error adding video caption track for ${props.id} (${lang}): ${captionsPath.value}`
        );
      }
    }

    updateTextTracks();
  }

  // TODO quality selector
  // player.qualityLevels().on('addqualitylevel', function (e) {
  //   console.log(e.qualityLevel);

  //   if (e.qualityLevel.height >= minHD) {
  //     highQualityAvailable.value = true;
  //   }
  // });

  // player.hlsQualitySelector({
  //   displayCurrentQuality: true
  // });

  const scroller = videoContainer.closest('.scroll-page__content');

  let onScreen = false;

  if (scroller) {
    viewportVideoST?.kill();
    viewportVideoST = ScrollTrigger.create({
      trigger: videoContainer,
      scroller,
      start: 'top center',
      end: 'bottom center',
      onEnter: self => {
        onScreen = true;
        // console.log(self);
        if (videoContainer.offsetHeight > 0) {
          setTimeout(() => {
            const player = getPlayer();
            if (
              player &&
              player.paused() &&
              onScreen &&
              canAutoPlay.value &&
              !suspend.reducedMotion
            ) {
              play(player);
            } else if (
              useFallbackPlayer.value &&
              fallbackEl &&
              !suspend.reducedMotion
            ) {
              fallbackEl.play();
            }
          }, 500);
        }
      },
      onLeave: self => {
        // console.log({ self });
        onScreen = false;
        getPlayer()?.pause();
        showOverlayNext(false);
        if (useFallbackPlayer.value && fallbackEl) {
          fallbackEl.pause();
        }
      },
      onLeaveBack: self => {
        // console.log({ self });
        onScreen = false;
        getPlayer()?.pause();
        showOverlayNext(false);
        if (useFallbackPlayer.value && fallbackEl) {
          fallbackEl.pause();
        }
      }
    });
  }

  if (disposeIfStale(player)) return;

  try {
    player.hlsQualitySelector();

    // swap the quality-button icon between HD/SD based on the active level.
    // selectedIndex follows adaptive bitrate switching, so the icon reflects
    // what is actually playing. The themed glyphs are masked via the
    // --vjs-quality-icon / --vjs-quality-sd-icon CSS vars (see <style>).
    const qualityLevels = (player as any).qualityLevels();
    const updateQualityIcon = () => {
      const controlBar = player?.getChild('controlBar');
      const btn = controlBar
        ?.el()
        ?.querySelector('.vjs-quality-selector .vjs-icon-hd');
      if (!btn) return;
      const lvl = qualityLevels[qualityLevels.selectedIndex];
      const isSD = !lvl || !(lvl.height >= minHD);
      btn.classList.toggle('-sd', isSD);
    };
    qualityLevels.on('change', updateQualityIcon);
    qualityLevels.on('addqualitylevel', updateQualityIcon);
    updateQualityIcon();
  } catch (e) {
    console.warn('hlsQualitySelector:', e);
  }

  const appEl = document.querySelector('#app');
  quizResetHandler = async (e: CustomEvent) => {
    if (stale()) return;
    if (await isQuizPage(e, pageId)) {
      set(`completed.${props.id}`, false);
      set(`completed.${sectionId}`, false);
    }
  };
  appEl?.addEventListener('quizReset', quizResetHandler);
}

onBeforeUnmount(() => {
  try {
    fullVideoPlayerTeardown();
  } catch (e) {
    console.error(e);
  }
});
</script>

<style>
#app .video-js {
  --h: 2.5em;

  @apply portrait_h-auto mx-auto aspect-video w-auto max-w-full bg-gray-600;

  font-size: 0.875rem;

  /* trim default control button width to tighten inline spacing
     (buttons only - leaves progress bar / volume panel untouched) */
  .vjs-button:not(.vjs-play-control) {
    width: 2.75em;
  }

  .vjs-control-bar > .vjs-button {
    background: transparent;
    transition: background-color 0.6s;
  }

  .vjs-button:not([disabled]):hover {
    background-color: rgba(0 0 0 / 0.6);
    transition: background-color 0.2s;
  }

  .vjs-control-bar {
    padding-inline-end: 0.5em;
    margin: 0.5em;
    border-radius: 3px;
    width: auto;
    height: var(--h);
  }

  .vjs-texttrack-settings,
  .vjs-subs-caps-button,
  .vjs-picture-in-picture-control {
    display: none !important;
  }

  /* hide some controls on small devices*/
  .vjs-volume-panel,
  .vjs-remaining-time,
  .vjs-quality-selector {
    @apply sm_flex hidden;
  }

  .vjs-menu-item {
    text-transform: initial;
  }

  .vjs-subtitles-menu-item {
    display: none;
  }

  .vjs-big-play-button {
    inset: 0;
    margin: auto;
  }

  .vjs-error-display:before {
    content: '\2716';
  }

  .vjs-modal-dialog-content {
    display: none;
  }

  .vjs-time-control {
    width: auto;
  }

  .vjs-time-control {
    width: auto;
    height: var(--h);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.75em;
    line-height: 1;
  }

  .vjs-remaining-time-display {
    line-height: 1;
    display: block;
  }

  .vjs-control:focus:before,
  .vjs-control:hover:before,
  .vjs-control:focus {
    text-shadow: none;
  }

  .vjs-volume-panel:has(:focus-visible),
  .vjs-progress-control:has(:focus-visible) {
    text-shadow: 0em 0em 1em white;
    --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0
      var(--tw-ring-offset-width) var(--tw-ring-offset-color);
    --tw-ring-shadow: var(--tw-ring-inset) 0 0 0
      calc(3px + var(--tw-ring-offset-width)) var(--tw-ring-color);
    box-shadow:
      var(--tw-ring-offset-shadow), var(--tw-ring-shadow),
      var(--tw-shadow, 0 0 #0000);
  }

  .vjs-control,
  .vjs-menu-button {
    &:focus-visible:before,
    &:hover:before,
    &:focus-visible {
      text-shadow: 0em 0em 1em white;
      @apply focus-visible_ring;
    }
  }

  /* themed control icons: replace the video.js icon font with masked SVGs
     driven by the --vjs-*-icon vars (see videoIconVars / iconSets.yml).
     flexbox centring resets video.js's own icon-font positioning. scoped to
     buttons so the volume slider / progress bar layout is left untouched */
  .vjs-button,
  .vjs-menu-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .vjs-button .vjs-icon-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .vjs-play-control .vjs-icon-placeholder:before,
  .vjs-mute-control .vjs-icon-placeholder:before,
  .vjs-fullscreen-control .vjs-icon-placeholder:before,
  .vjs-captions-button .vjs-icon-placeholder:before,
  .vjs-transcript-button .vjs-icon-placeholder:before,
  .vjs-playback-rate .vjs-icon-placeholder:before,
  .vjs-icon-hd:before {
    content: '';
    position: static;
    display: block;
    width: 1em;
    height: 1em;
    margin: 0;
    line-height: 1;
    transform: none;
    background-color: currentColor;
    -webkit-mask: var(--_vjs-icon) no-repeat center / contain;
    mask: var(--_vjs-icon) no-repeat center / contain;
  }

  /* per-control + per-state icon selection */
  .vjs-play-control .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-play-icon);
  }
  .vjs-play-control.vjs-playing .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-pause-icon);
  }
  .vjs-mute-control .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-volume-icon);
  }
  .vjs-mute-control.vjs-vol-0 .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-mute-icon);
  }
  .vjs-fullscreen-control .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-fullscreen-icon);
  }
  &.vjs-fullscreen .vjs-fullscreen-control .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-fullscreen-exit-icon);
  }
  .vjs-icon-hd:before {
    --_vjs-icon: var(--vjs-quality-icon);
  }
  /* swap to the SD icon when the active quality level is below minHD
     (the -sd class is toggled in runPlayerSetup off qualityLevels changes) */
  .vjs-icon-hd.-sd:before {
    --_vjs-icon: var(--vjs-quality-sd-icon);
  }
  .vjs-captions-button .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-captions-icon);
  }
  .vjs-transcript-button .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-transcript-icon);
  }
  .vjs-playback-rate .vjs-icon-placeholder:before {
    --_vjs-icon: var(--vjs-speedometer-icon);
  }

  .vjs-control {
    order: 0;
  }

  .vjs-play-control {
    order: 0;
  }

  .vjs-volume-panel {
    order: 1;
  }

  .vjs-progress-control {
    order: 2;
  }

  .vjs-remaining-time {
    order: 3;
  }

  .vjs-fullscreen-control {
    order: 4;
  }

  .vjs-quality-selector {
    order: 5;
  }

  .vjs-time-control:not(.vjs-remaining-time) {
    display: none !important;
  }

  .vjs-playback-rate {
    order: 6;

    /* hide the "1x" text label and show a speedometer icon instead */
    .vjs-playback-rate-value {
      display: none;
    }

    & > .vjs-menu-button {
      position: static;
      width: 100%;
      height: 100%;
    }
  }

  /* Volume icon is visually larger than the other SVG masks */
  .vjs-mute-control .vjs-icon-placeholder:before {
    width: 0.85em;
    height: 0.85em;
  }

  .vjs-captions-button {
    order: 7;

    /* hide the captions button on Safari (mobile and desktop) */
    @supports (-webkit-backdrop-filter: none) {
      display: none;
    }
  }

  .vjs-transcript-button {
    order: 7;
  }

  .vjs-text-track-display {
    --auto-alpha-dur-0: 0.1s;
    --auto-alpha-dur-1: 0.1s;
    transform-origin: center bottom;

    visibility: inherit;
    opacity: 1;
    transition:
      opacity var(--auto-alpha-dur-1) linear,
      transform var(--auto-alpha-dur-1),
      visibility 0s step-start;
    transition-duration: var(--auto-alpha-dur-1);
  }

  .vjs-menu-button-popup .vjs-menu .vjs-menu-content {
    bottom: 1em;
    max-height: 20em;
  }
}

#app {
  .-hide-captions .vjs-text-track-display {
    visibility: hidden;
    opacity: 0;
    transition:
      opacity var(--auto-alpha-dur-0) linear,
      transform var(--auto-alpha-dur-0),
      visibility 0s step-end;
    transition-duration: var(--auto-alpha-dur-0);
    transform: scale(0.95);
  }

  /**
    toggle opacity when top-level "hide" class is added
   */
  .-hide-captions .vjs-captions-button,
  .-hide-transcript .vjs-transcript-button {
    opacity: 0.5;
  }

  /**
    hide both captions and transcript button if no captions
   */
  .-no-captions-available .vjs-captions-button,
  .-no-captions-available .vjs-transcript-button {
    display: none;
  }
}

/*.video-js.force-video-completion .vjs-remaining-time {
  visibility: hidden;
}*/

.video-container .cue {
  &:focus {
    @apply bg-primary-200/40 dark_bg-primary-500/30;
  }

  &.active {
    @apply bg-primary-200 dark_bg-primary-500/50;
  }
}

#app .video-fallback video {
  @apply portrait_h-auto mx-auto aspect-video w-auto max-w-full bg-gray-600;

  font-size: 0.875rem;
}
</style>
